import { useMutation } from "@apollo/client";
import ApprovalOutlined from "@mui/icons-material/ApprovalOutlined";
import { ListItemIcon, ListItemText, MenuItem, Tooltip } from "@mui/material";
import { useAlerts } from "components/Alerts/AlertProvider";
import { ReportForDefaultDate } from "components/verifyAndSubmitDefaultDate";
import { VerifyAndSubmitFormDialog } from "components/VerifyAndSubmitFormDialog";
import { hasValue } from "encamp-shared/src/utils/hasValue";
import { TierIiReportEncampStatus, TierIiReportVerifierInput } from "generated-graphql/graphql";
import { BULK_VERIFY_MUTATION } from "queries/tierIIVerification";
import { useCallback, useMemo } from "react";

import { useBulkActionContext } from ".";
import { Row } from "../ChemicalFacilitiesDataGrid";

function toReportsForDefaultDate(rows: Row[], reportingYear: number): ReportForDefaultDate[] {
  return rows.map((row) => ({
    reportKind: row.reportKind,
    reportingYear,
    facilityState: row.state,
  }));
}

export function VerifyAndSubmitMenuItem() {
  const { setDialogState, selectedRows } = useBulkActionContext();
  const spansOrganizations = new Set(selectedRows.map((row) => row.tenantId)).size > 1;

  const menuItem = (
    <MenuItem disabled={spansOrganizations} onClick={() => setDialogState("verify&submit")}>
      <ListItemIcon>
        <ApprovalOutlined />
      </ListItemIcon>
      <ListItemText>Verify & Submit</ListItemText>
    </MenuItem>
  );
  // The wrapper would break arrow-key navigation, so only add it when the item is disabled.
  if (!spansOrganizations) return menuItem;

  return (
    <Tooltip title="Select reports from one organization to verify them together" placement="left">
      {/* A disabled menu item ignores hover, so the tooltip needs a wrapper. */}
      <span>{menuItem}</span>
    </Tooltip>
  );
}

export function VerifyAndSubmitDialog() {
  const alerts = useAlerts();
  const { dialogState, onClose, selectedRows, reportingYear } = useBulkActionContext();

  const verifiableRows = useMemo(
    () =>
      selectedRows.filter(
        (row) => row.encampStatus === TierIiReportEncampStatus.AwaitingVerification
      ),
    [selectedRows]
  );

  const skippedDueToVerificationCount = selectedRows.length - verifiableRows.length;

  const reportsWithValidationErrorsCount = useMemo(
    () => selectedRows.filter((row) => row.issues > 0).length,
    [selectedRows]
  );
  const verifiableRowsWithoutOpenReviews = useMemo(
    () => verifiableRows.filter((row) => !row.hasIncompleteReviews),
    [verifiableRows]
  );
  const verifiableRowsWithoutOpenReviewsCount = verifiableRowsWithoutOpenReviews.length;

  const skippedDueToOpenReviewsCount =
    verifiableRows.length - verifiableRowsWithoutOpenReviews.length;

  const [mutate, { loading }] = useMutation(BULK_VERIFY_MUTATION, {
    refetchQueries: ["TierIIReportActivities"],
  });

  const submit = useCallback(
    async (verifier: TierIiReportVerifierInput, overrideOpenReviews: boolean) => {
      const reportIds = (overrideOpenReviews ? verifiableRows : verifiableRowsWithoutOpenReviews)
        .map((row) => row.tierIIReportId)
        .filter(hasValue);

      try {
        await mutate({ variables: { reportIds, verifier } });
        alerts.info("Starting bulk verification.");
        onClose();
      } catch (err) {
        alerts.error("An error occurred during bulk verification", err);
      }
    },
    [alerts, mutate, onClose, verifiableRows, verifiableRowsWithoutOpenReviews]
  );

  const reportIds = verifiableRows.map((row) => row.tierIIReportId).filter(hasValue);

  const reports = useMemo(
    () => toReportsForDefaultDate(verifiableRowsWithoutOpenReviews, reportingYear),
    [verifiableRowsWithoutOpenReviews, reportingYear]
  );
  const overrideReports = useMemo(
    () => toReportsForDefaultDate(verifiableRows, reportingYear),
    [verifiableRows, reportingYear]
  );

  return (
    <VerifyAndSubmitFormDialog
      withConfirmation
      open={dialogState === "verify&submit"}
      onClose={onClose}
      loading={loading}
      onSubmit={submit}
      skippedDueToOpenReviewsCount={skippedDueToOpenReviewsCount}
      skippedDueToVerificationCount={skippedDueToVerificationCount}
      verifiableRowsWithoutOpenReviewsCount={verifiableRowsWithoutOpenReviewsCount}
      reportsWithValidationErrorsCount={reportsWithValidationErrorsCount}
      reports={reports}
      overrideReports={overrideReports}
      reportIds={reportIds}
    />
  );
}
